// C:\Users\purplehat\Desktop\payment-cookie-system\frontend\src\components\AmazonAccountCreator.js

import React, { useState, useEffect, useRef } from 'react';
import { PlayIcon, CheckCircleIcon, ShieldExclamationIcon, ArrowPathIcon, EyeIcon, DocumentDuplicateIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import socket from '../services/socket';
import toast from 'react-hot-toast';

function AmazonAccountCreator() {
  const [progress, setProgress] = useState({ step: 'idle', message: 'Listo para crear cuenta', steps: [] });
  const [captchaDetected, setCaptchaDetected] = useState(false);
  const [latestScreenshot, setLatestScreenshot] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);
  const [showCookie, setShowCookie] = useState(false);
  const [smsCode, setSmsCode] = useState(null);
  const [password, setPassword] = useState('Amazon2024!');
  const [puzzleInstructions, setPuzzleInstructions] = useState('');
  const [lastKeyPressed, setLastKeyPressed] = useState(null);
  const logContainerRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  // Registrar listener de SOCKET solo UNA VEZ
  useEffect(() => {
    console.log('🟢 Registrando listener de amazon-account-progress');
    
    const handleProgress = (data) => {
      console.log('📡 EVENTO RECIBIDO EN HANDLER:', data);
      const { step, data: stepData } = data;
      
      setProgress(prev => {
        const newSteps = [...prev.steps];
        if (!newSteps.find(s => s.step === step)) {
          newSteps.push({ 
            step, 
            message: stepData.message, 
            timestamp: data.timestamp || new Date().toISOString(), 
            icon: getStepIcon(step), 
            color: getStepColor(step), 
            data: stepData 
          });
        }
        return { step, message: stepData.message, steps: newSteps };
      });

      if (stepData.screenshot) {
        console.log('📸 Screenshot recibido');
        setLatestScreenshot(stepData.screenshot);
      }
      
      if (step === 'puzzle_redirect') {
        console.log('🎯 PUZZLE REDIRECT - Mostrando puzzle!');
        setCaptchaDetected(true);
        setPuzzleInstructions(stepData.instructions || 'Usa las flechas ↑ ↓ ← → para mover la pieza');
        toast.info('🎯 Puzzle detectado!');
      }
      
      if (step === 'puzzle_update' && stepData.screenshot) {
        console.log('🔄 Actualizando puzzle');
        setLatestScreenshot(stepData.screenshot);
      }
      
      if (step === 'sms_recibido' && stepData.code) {
        setSmsCode(stepData.code);
        setCaptchaDetected(false);
      }
      
      if (step === 'completado' && stepData.account) {
        setIsCreating(false); 
        setCreatedAccount(stepData.account);
        setCaptchaDetected(false); 
        toast.success('🎉 Cuenta creada!');
      }
      
      if (step === 'error') {
        setIsCreating(false); 
        setCaptchaDetected(false);
        toast.error(`❌ ${stepData.message}`);
      }
    };

    // Registrar el listener
    socket.on("amazon-account-progress", handleProgress);
    setIsListening(true);

    // Unirse al usuario
    const token = localStorage.getItem("token");
    if (token) {
      api.get("/auth/profile").then((response) => {
        const user = response.data.user || response.data;
        const userId = user?._id || user?.id;
        if (userId && socket.isConnected()) {
          console.log('🔗 Uniendo usuario:', userId);
          socket.emit("join-user", userId);
        }
      }).catch((err) => console.error('Error:', err));
    }
    
    return () => { 
      console.log('🔴 Removiendo listener');
      socket.off("amazon-account-progress", handleProgress);
    };
  }, []);

  // Capturar teclas globalmente cuando hay puzzle
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (!captchaDetected) return;
      
      const key = e.key;
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
      
      if (arrowKeys.includes(key)) {
        e.preventDefault();
        console.log(`🎮 Tecla presionada: ${key}`);
        
        setLastKeyPressed(key);
        setTimeout(() => setLastKeyPressed(null), 200);
        
        sendKeyToBrowser(key);
        
        if (key === 'Enter') {
          handlePuzzleResolved();
        }
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [captchaDetected]);

  const sendKeyToBrowser = async (key) => {
    try {
      await api.post('/amazon-accounts/send-key', { key });
    } catch (error) {
      console.error('Error enviando tecla:', error);
    }
  };

  const handleCreate = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user._id || user.id;
    
    console.log('🚀 Iniciando creación de cuenta...');
    console.log('   userId:', userId);
    console.log('   Socket conectado:', socket.isConnected());
    console.log('   Listener registrado:', isListening);
    
    setIsCreating(true);
    setProgress({ step: 'iniciando', message: 'Iniciando registro...', steps: [] });
    setCaptchaDetected(false); 
    setLatestScreenshot(null); 
    setCreatedAccount(null); 
    setSmsCode(null);
    
    try {
      const res = await api.post('/amazon-accounts/create', { 
        password: password || 'Amazon2024!', 
        userId, 
        requestId: Date.now().toString() 
      });
      console.log('📥 Respuesta:', res.data);
      if (!res.data.success) { 
        toast.error(res.data?.error || 'Error'); 
        setIsCreating(false); 
      }
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error(error.response?.data?.error || 'Error de conexión');
      setIsCreating(false);
    }
  };

  const handlePuzzleResolved = async () => {
    try {
      console.log('✅ Enviando resolución de puzzle...');
      await api.post('/amazon-accounts/continue');
      setCaptchaDetected(false);
      toast.success('✅ Puzzle resuelto!');
    } catch (e) { 
      console.error('Error:', e);
      toast.error('Error al continuar'); 
    }
  };

  const handleCloseBrowser = async () => {
    try { 
      await api.post('/amazon-accounts/close-browser'); 
      setIsCreating(false); 
      setCaptchaDetected(false);
    } catch (e) {}
  };

  const copyCookie = () => { 
    if (createdAccount?.cookieString) { 
      navigator.clipboard.writeText(createdAccount.cookieString); 
      toast.success('Cookie copiada!'); 
    } 
  };
  
  const copySmsCode = () => { 
    if (smsCode) { 
      navigator.clipboard.writeText(smsCode); 
      toast.success('Código copiado!'); 
    } 
  };

  function getStepIcon(step) {
    const icons = { 
      'obteniendo_numero':'📱','numero_obtenido':'✅','abriendo_navegador':'🌐',
      'ingresando_telefono':'📱','formulario_lleno':'✅','esperando_sms':'⏳',
      'sms_recibido':'📩','completado':'🎉','captcha_detectado':'🛑','error':'❌',
      'screen_stream':'🖼️','puzzle_redirect':'🎯','puzzle_update':'🔄',
      'otp':'📩','direccion':'📍','wallet':'💳','cookies':'🍪','sms_timeout':'⚠️'
    };
    return icons[step] || '⏳';
  }
  
  function getStepColor(step) {
    const colors = {
      'captcha_detectado':'#ff9900','puzzle_redirect':'#ff6600','error':'#ff0033',
      'completado':'#00ff88','sms_recibido':'#ccff00'
    };
    return colors[step] || '#9ca3af';
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff', padding: '1rem', paddingBottom: '6rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: '#0ff', cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>🤖 Crear Cuenta Amazon</h1>
          <p style={{ fontSize: '0.75rem', color: '#f90', margin: '0.25rem 0 0' }}>
            {captchaDetected ? '🎯 PUZZLE ACTIVO - Usa las flechas' : 'Registro humano · Puzzle interactivo'}
          </p>
        </div>
      </div>

      {!isCreating && !createdAccount && (
        <div style={{ background: '#111', border: '1px solid #333', borderRadius: '1rem', padding: '1rem', marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>🔑 Contraseña</label>
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} 
            style={{ width: '100%', padding: '0.75rem', background: '#000', border: '1px solid #333', borderRadius: '0.5rem', color: '#fff', fontSize: '1rem' }} />
        </div>
      )}

      <div style={{ background: '#111', border: `1px solid ${captchaDetected ? '#f90' : isCreating ? '#0ff' : createdAccount ? '#0f8' : '#333'}`, borderRadius: '1rem', padding: '1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          {isCreating && !captchaDetected ? 
            <ArrowPathIcon style={{ width: '1.5rem', height: '1.5rem', color: '#0ff', animation: 'spin 1s linear infinite' }} /> :
           createdAccount ? 
            <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: '#0f8' }} /> :
           captchaDetected ? 
            <ShieldExclamationIcon style={{ width: '1.5rem', height: '1.5rem', color: '#f90' }} /> :
            <PlayIcon style={{ width: '1.5rem', height: '1.5rem', color: '#9ca3af' }} />}
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {captchaDetected ? '🎯 ¡PUZZLE ACTIVO! Resuelve usando flechas' : progress.message}
          </span>
        </div>

        {captchaDetected && (
          <div style={{ background: '#1a0a00', border: '2px solid #f90', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', textAlign: 'center', animation: 'pulse 1.5s infinite' }}>
            <ShieldExclamationIcon style={{ width: '2rem', height: '2rem', color: '#f90', marginBottom: '0.25rem' }} />
            <p style={{ color: '#f90', fontWeight: 'bold', fontSize: '1rem' }}>🎯 PUZZLE DE AMAZON</p>
            <p style={{ color: '#ff6600', fontSize: '0.9rem' }}>{puzzleInstructions}</p>
            
            {lastKeyPressed && (
              <div style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#f90',
                color: '#000',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                borderRadius: '8px',
                marginBottom: '0.5rem'
              }}>
                {lastKeyPressed === 'ArrowUp' && '⬆️'}
                {lastKeyPressed === 'ArrowDown' && '⬇️'}
                {lastKeyPressed === 'ArrowLeft' && '⬅️'}
                {lastKeyPressed === 'ArrowRight' && '➡️'}
                {lastKeyPressed === 'Enter' && '✅ ENTER'}
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', maxWidth: '200px', margin: '0.5rem auto' }}>
              <div></div>
              <button onClick={() => sendKeyToBrowser('ArrowUp')} style={{ padding: '0.75rem', background: '#333', border: '2px solid #f90', borderRadius: '0.75rem', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>↑</button>
              <div></div>
              <button onClick={() => sendKeyToBrowser('ArrowLeft')} style={{ padding: '0.75rem', background: '#333', border: '2px solid #f90', borderRadius: '0.75rem', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>←</button>
              <button onClick={() => sendKeyToBrowser('ArrowDown')} style={{ padding: '0.75rem', background: '#333', border: '2px solid #f90', borderRadius: '0.75rem', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>↓</button>
              <button onClick={() => sendKeyToBrowser('ArrowRight')} style={{ padding: '0.75rem', background: '#333', border: '2px solid #f90', borderRadius: '0.75rem', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>→</button>
            </div>
            
            <button onClick={handlePuzzleResolved} style={{ width: '100%', padding: '0.75rem', background: '#0f0', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 'bold', cursor: 'pointer', marginTop: '0.5rem' }}>
              ✅ YA RESOLVÍ - CONTINUAR
            </button>
          </div>
        )}

        {smsCode && (
          <div style={{ background: '#0a1a00', border: '1px solid #cf0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem' }}>CÓDIGO SMS</p>
            <p style={{ fontSize: '2rem', fontFamily: 'monospace', color: '#cf0', fontWeight: 'bold', margin: '0.25rem 0' }}>{smsCode}</p>
            <button onClick={copySmsCode} style={{ padding: '0.25rem 0.75rem', background: '#1a3a00', border: '1px solid #cf0', borderRadius: '0.5rem', color: '#cf0', cursor: 'pointer' }}>📋 Copiar</button>
          </div>
        )}

        {!isCreating && !createdAccount && (
          <button onClick={handleCreate} style={{ width: '100%', padding: '1rem', background: 'linear-gradient(135deg, #f90, #f60)', border: 'none', borderRadius: '0.75rem', color: '#000', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' }}>
            🚀 Iniciar Registro
          </button>
        )}
        
        {isCreating && !captchaDetected && (
          <div style={{ textAlign: 'center' }}>
            <button disabled style={{ width: '100%', padding: '1rem', background: '#333', border: 'none', borderRadius: '0.75rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
              ⏳ Registro en progreso...
            </button>
            <button onClick={handleCloseBrowser} style={{ padding: '0.5rem 1rem', background: '#f03', border: 'none', borderRadius: '0.5rem', color: '#fff', cursor: 'pointer' }}>
              ❌ Cancelar
            </button>
          </div>
        )}
        
        {createdAccount && (
          <button onClick={() => { setCreatedAccount(null); handleCreate(); }} style={{ width: '100%', padding: '1rem', background: '#f90', border: 'none', borderRadius: '0.75rem', color: '#000', fontWeight: 'bold', cursor: 'pointer' }}>
            🔄 Crear Otra Cuenta
          </button>
        )}
      </div>

      {latestScreenshot && (
        <div style={{ background: '#111', border: `1px solid ${captchaDetected ? '#f90' : '#333'}`, borderRadius: '1rem', overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ padding: '0.5rem 1rem', borderBottom: `1px solid ${captchaDetected ? '#f90' : '#333'}`, background: '#1a1a1a' }}>
            <span style={{ fontSize: '0.75rem', color: captchaDetected ? '#f90' : '#9ca3af' }}>
              {captchaDetected ? '🎯 PUZZLE EN VIVO' : '📸 Vista del navegador'}
            </span>
          </div>
          <img src={`data:image/jpeg;base64,${latestScreenshot}`} alt="Browser" style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }} />
        </div>
      )}

      <div style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', overflow: 'hidden' }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #222', background: '#1a1a1a' }}>
          <span style={{ color: '#f90', fontWeight: 'bold' }}>📋 Progreso</span>
        </div>
        <div ref={logContainerRef} style={{ maxHeight: '300px', overflowY: 'auto', padding: '0.5rem' }}>
          {progress.steps.length === 0 ? 
            <div style={{ textAlign: 'center', padding: '2rem', color: '#555' }}>Presiona "Iniciar Registro"</div> :
            progress.steps.map((s, i) => (
              <div key={i} style={{ padding: '0.5rem', borderBottom: '1px solid #1a1a1a', fontSize: '0.8rem' }}>
                {s.message}
              </div>
            ))
          }
        </div>
      </div>

      {createdAccount && (
        <div style={{ background: '#0a1a0a', border: '2px solid #0f8', borderRadius: '1rem', padding: '1.5rem', marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <CheckCircleIcon style={{ width: '2rem', height: '2rem', color: '#0f8' }} />
            <h3 style={{ color: '#0f8', margin: 0 }}>¡Cuenta Creada!</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ background: '#111', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>📱 Teléfono</div>
              <div style={{ fontFamily: 'monospace', color: '#0ff' }}>{createdAccount.phoneNumber}</div>
            </div>
            <div style={{ background: '#111', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>🍪 Cookies</div>
              <div style={{ fontFamily: 'monospace', color: '#0ff' }}>{createdAccount.cookiesCount}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setShowCookie(!showCookie)} style={{ flex: 1, padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', color: '#0ff', cursor: 'pointer' }}>
              {showCookie ? 'Ocultar' : 'Ver Cookie'}
            </button>
            <button onClick={copyCookie} style={{ flex: 1, padding: '0.75rem', background: '#f90', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 'bold', cursor: 'pointer' }}>
              Copiar Cookie
            </button>
          </div>
          {showCookie && createdAccount.cookieString && (
            <div style={{ marginTop: '1rem', background: '#000', border: '1px solid #333', borderRadius: '0.5rem', padding: '0.75rem', maxHeight: '200px', overflowY: 'auto' }}>
              <code style={{ fontSize: '0.7rem', color: '#0f8' }}>{createdAccount.cookieString}</code>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { border-color:#f90; box-shadow:0 0 10px rgba(255,153,0,0.3); } 50% { border-color:#f60; box-shadow:0 0 20px rgba(255,102,0,0.6); } }
      `}</style>
    </div>
  );
}

export default AmazonAccountCreator;